import { Link, useParams } from 'react-router-dom';
import { useInvitationByToken, useAcceptInvitation, useRejectInvitation } from '../hooks/useCoOwnershipInvitations';

const getErrorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

const getErrorDetail = (err: unknown): string | undefined =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

const InvitationTokenPage = () => {
  const { token } = useParams<{ token: string }>();

  const { data: invitation, isLoading, isError, error } = useInvitationByToken(token ?? '');
  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
      </div>
    );
  }

  if (isError) {
    const status = getErrorStatus(error);
    const message =
      status === 403
        ? 'This invitation was not sent to you.'
        : 'Invitation not found or no longer valid.';

    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <p className="text-gray-700 mb-4">{message}</p>
          <Link to="/my-invitations" className="text-primary-600 hover:underline text-sm">
            View all invitations
          </Link>
        </div>
      </div>
    );
  }

  if (acceptMutation.isSuccess) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center space-y-4">
          <p className="text-green-700 font-medium">Co-ownership accepted!</p>
          <p className="text-sm text-gray-500">You now have access to this apartment.</p>
          <Link to="/apartments" className="inline-block py-2 px-4 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors">
            Go to my apartments
          </Link>
        </div>
      </div>
    );
  }

  if (rejectMutation.isSuccess) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center space-y-4">
          <p className="text-gray-700 font-medium">Co-ownership rejected.</p>
          <Link to="/my-invitations" className="text-primary-600 hover:underline text-sm">
            View all invitations
          </Link>
        </div>
      </div>
    );
  }

  const acceptError = acceptMutation.isError
    ? (getErrorStatus(acceptMutation.error) === 400
        ? 'This invitation has already been actioned.'
        : getErrorDetail(acceptMutation.error) ?? 'Something went wrong, please try again')
    : null;

  const rejectError = rejectMutation.isError
    ? (getErrorStatus(rejectMutation.error) === 400
        ? 'This invitation has already been actioned.'
        : getErrorDetail(rejectMutation.error) ?? 'Something went wrong, please try again')
    : null;

  return (
    <div className="max-w-md mx-auto mt-12">
      <div className="bg-white border border-gray-200 rounded-xl p-8 space-y-6">
        <h1 className="text-xl font-bold text-gray-900">Co-Ownership Invitation</h1>

        {invitation && (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <span className="font-medium">Apartment: </span>
              {invitation.apartmentName}
            </p>
            <p>
              <span className="font-medium">Invited by: </span>
              {invitation.invitingOwnerEmail}
            </p>
          </div>
        )}

        {(acceptError || rejectError) && (
          <p className="text-sm text-red-600">{acceptError ?? rejectError}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => token && invitation && acceptMutation.mutate({ apartmentId: invitation.apartmentId, token })}
            disabled={acceptMutation.isPending || rejectMutation.isPending}
            className="flex-1 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {acceptMutation.isPending ? 'Accepting…' : 'Accept'}
          </button>
          <button
            onClick={() => token && invitation && rejectMutation.mutate({ apartmentId: invitation.apartmentId, token })}
            disabled={acceptMutation.isPending || rejectMutation.isPending}
            className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitationTokenPage;
